import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, LogIn, UserCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BrandMark, BRAND_PROGRAM } from "../components/Brand";

const Landing = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleBegin = () => {
    if (user) navigate("/quiz");
    else navigate("/login", { state: { from: "/quiz" } });
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="landing-page">
      {/* Top bar */}
      <div className="border-b border-[#E8E2D9]/60">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="inline-flex items-center gap-3">
            <BrandMark className="h-9 sm:h-10 w-auto" />
            <span className="hidden sm:inline-block h-7 w-px bg-[#E8E2D9]" aria-hidden="true" />
            <Link
              to="/"
              className="hidden sm:inline font-serif-display text-sm sm:text-base text-[#2D2A26] font-semibold hover:text-[#C86B5E] transition-colors"
            >
              {BRAND_PROGRAM}
            </Link>
          </div>

          <div className="flex items-center gap-3" data-testid="landing-auth-area">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="landing-admin-link"
                    className="text-xs sm:text-sm font-semibold text-[#C86B5E] hover:underline"
                  >
                    Admin
                  </Link>
                )}
                <span className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#5C5A56]">
                  <UserCircle className="w-4 h-4" />
                  {user.name || user.email}
                </span>
                <button
                  onClick={logout}
                  data-testid="landing-logout-button"
                  className="text-sm font-medium text-[#2D2A26] border-2 border-[#E8E2D9] hover:border-[#D1C7B8] rounded-full px-4 py-1.5 bg-white transition-all"
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="landing-login-link"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2D2A26] border-2 border-[#E8E2D9] hover:border-[#C86B5E] rounded-full px-4 py-1.5 bg-white transition-all"
              >
                <LogIn className="w-3.5 h-3.5" />
                Sign in
              </Link>
            )}
          </div>
        </div>
      </div>

      <main className="flex-1 flex items-center">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="flex items-center gap-2 mb-8">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A8782] font-semibold">
                Research-based reflection · Common Ground Institute
              </span>
            </div>

            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#2D2A26] leading-[1.05] tracking-tight font-semibold max-w-3xl">
              How healthy is your relationship?
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-[#5C5A56] leading-relaxed max-w-2xl">
              A short, 24-question check-in across six dimensions of connection,
              communication and care. Honest answers — no judgment, no diagnosis.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={handleBegin}
                data-testid="start-quiz-button"
                className="group inline-flex items-center gap-2 bg-[#C86B5E] text-white rounded-full px-8 py-4 font-medium text-base hover:bg-[#B35B4E] transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
              >
                {user ? "Begin the check-in" : "Sign in & begin"}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
              </button>
              <span className="text-sm text-[#8A8782]">Takes about 5 minutes · Private · PDF report</span>
            </div>

            <div className="mt-16 grid sm:grid-cols-3 gap-6 md:gap-8 max-w-3xl">
              {[
                { n: "24", l: "questions" },
                { n: "6", l: "dimensions" },
                { n: "2×2", l: "result map" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-[#E8E2D9] pl-4">
                  <div className="font-serif-display text-3xl text-[#2D2A26]">{s.n}</div>
                  <div className="text-sm text-[#8A8782] uppercase tracking-wider mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="border-t border-[#E8E2D9] bg-white/60">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-[#8A8782] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="text-xs text-[#8A8782] leading-relaxed" data-testid="disclaimer-text">
            This quiz is for reflection and education only. It is not a clinical diagnosis or a
            substitute for counseling, therapy, or professional advice.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
